<template>
    <div class="business-lookup">
        <v-layout v-if="!showCreateForm" row align-end>
            <v-flex>
                <v-autocomplete
                    v-model="selected"
                    :items="results"
                    item-text="name"
                    item-value="businessID"
                    return-object
                    :label="label"
                    outline
                    dense
                    clearable
                    hide-no-data
                    hide-details
                    :loading="loading"
                    autocomplete="new-password"
                    name="ecb-employer-business"
                    @change="onSelectedChange"
                    @focus="disableBrowserAutofill"
                ></v-autocomplete>
            </v-flex>
            <v-btn small outline color="success" class="ml-2 mb-1" @click="openCreateForm">
                <v-icon left small>mdi-plus</v-icon>
                New
            </v-btn>
        </v-layout>

        <v-card v-if="showCreateForm" class="business-lookup-create mt-2 pa-3">
            <div class="business-lookup-create-header">
                <v-icon small color="#2e7d32" class="mr-1">mdi-domain-plus</v-icon>
                New Business
            </div>
            <v-text-field
                v-model="createForm.name"
                label="Business Name *"
                prepend-inner-icon="mdi-domain"
                outline
                dense
                autocomplete="new-password"
                name="ecb-new-business-name"
                :error="createNameInvalid"
                :error-messages="createNameInvalid ? 'Business name is required.' : ''"
                class="mt-2"
            ></v-text-field>
            <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                    <v-text-field
                        v-model="createForm.contactPerson"
                        label="Contact Person"
                        prepend-inner-icon="mdi-account"
                        outline
                        dense
                    ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                    <v-combobox
                        v-model="createForm.contactPersonTitle"
                        :items="contactPersonTitleOptions"
                        label="Contact Title"
                        prepend-inner-icon="mdi-account-card-details"
                        outline
                        dense
                        hide-no-data
                    ></v-combobox>
                </v-flex>
            </v-layout>
            <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                    <v-text-field
                        v-model="createForm.phone"
                        label="Phone"
                        prepend-inner-icon="mdi-phone"
                        outline
                        dense
                    ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                    <v-text-field
                        v-model="createForm.email"
                        label="Email"
                        prepend-inner-icon="mdi-email"
                        outline
                        dense
                    ></v-text-field>
                </v-flex>
            </v-layout>
            <v-text-field
                v-model="createForm.address"
                label="Address"
                prepend-inner-icon="mdi-map-marker"
                outline
                dense
            ></v-text-field>
            <v-text-field
                v-model="createForm.ein"
                label="EIN"
                prepend-inner-icon="mdi-file-document-outline"
                outline
                dense
            ></v-text-field>
            <v-select
                :items="agents"
                item-text="name"
                item-value="agentID"
                v-model="createForm.agentID"
                label="Billing Agent"
                prepend-inner-icon="mdi-account-tie"
                outline
                dense
                clearable
                hide-details
            ></v-select>

            <div class="business-lookup-create-actions">
                <v-btn small color="default" dark @click="cancelCreateForm">Cancel</v-btn>
                <v-btn small color="success" :loading="creating" @click="createBusiness">
                    <v-icon left small>mdi-content-save</v-icon>
                    Create &amp; Select
                </v-btn>
            </div>
        </v-card>
    </div>
</template>
<script>
export default {
    name: "BusinessLookup",
    props: {
        value: [String, Number],
        label: {
            type: String,
            default: "Business",
        },
    },
    data() {
        return {
            loading: false,
            results: [],
            selected: null,
            showCreateForm: false,
            creating: false,
            createSubmitAttempted: false,
            agents: [],
            createForm: this.emptyCreateForm(),
            contactPersonTitleOptions: [
                "Owner",
                "President",
                "CEO",
                "CFO",
                "Controller",
                "Office Manager",
                "HR Manager",
                "Payroll Manager",
                "Bookkeeper",
                "Accountant",
                "Manager",
            ],
        };
    },
    computed: {
        createNameInvalid() {
            return (
                this.createSubmitAttempted && !(this.createForm.name || "").trim()
            );
        },
    },
    created() {
        this.loadResults();
        this.loadAgents();
    },
    methods: {
        disableBrowserAutofill(event) {
            const input = event && event.target;
            if (!input) return;

            input.setAttribute("autocomplete", "new-password");
            input.setAttribute("name", "ecb-employer-business");
        },
        emptyCreateForm() {
            return {
                name: "",
                contactPerson: "",
                contactPersonTitle: "",
                phone: "",
                email: "",
                address: "",
                ein: "",
                agentID: null,
            };
        },
        async loadAgents() {
            try {
                const response = await this.$http.get("api/businesses/getAgents");
                this.agents = Array.isArray(response) ? response : [];
            } catch (e) {
                this.agents = [];
            }
        },
        async loadResults() {
            this.loading = true;

            try {
                const results = await this.$http.get("api/businesses/searchBusinesses");
                this.results = Array.isArray(results) ? results : [];
            } catch (e) {
                this.results = [];
            }

            this.loading = false;
        },
        onSelectedChange(business) {
            this.$emit("input", business ? business.businessID : null);
            this.$emit("selected", business || null);
        },
        openCreateForm() {
            this.showCreateForm = true;
            this.createSubmitAttempted = false;
            this.createForm = this.emptyCreateForm();
        },
        cancelCreateForm() {
            this.showCreateForm = false;
            this.createForm = this.emptyCreateForm();
        },
        async createBusiness() {
            this.createSubmitAttempted = true;

            const name = (this.createForm.name || "").trim();

            if (!name) {
                this.$notify({
                    text: "Business name is required",
                    group: "notification",
                    type: "error",
                });
                return;
            }

            try {
                this.creating = true;

                const response = await this.$http.post("api/businesses/save", {
                    businessID: 0,
                    name,
                    contactPerson: this.createForm.contactPerson,
                    contactPersonTitle: this.createForm.contactPersonTitle,
                    phone: this.createForm.phone,
                    email: this.createForm.email,
                    address: this.createForm.address,
                    ein: this.createForm.ein,
                    agentID: this.createForm.agentID || null,
                });

                const newBusiness = Object.assign(
                    { businessID: response.businessID, isNew: true },
                    this.createForm,
                    { name }
                );

                this.results = [newBusiness].concat(
                    this.results.filter((b) => b.businessID !== newBusiness.businessID)
                );
                this.selected = newBusiness;
                this.showCreateForm = false;

                this.$notify({
                    text: "Business created successfully",
                    group: "notification",
                    type: "success",
                });

                this.onSelectedChange(newBusiness);
            } catch (e) {
                this.$notify({
                    text: this.getErrorMessage(e, "Failed to create business"),
                    group: "notification",
                    type: "error",
                });
            } finally {
                this.creating = false;
            }
        },
        getErrorMessage(error, fallback) {
            if (error && error.Message) return error.Message;
            if (error && error.message) return error.message;
            if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.Message
            )
                return error.response.data.Message;

            return fallback;
        },
        clear() {
            this.selected = null;
            this.showCreateForm = false;
            this.createForm = this.emptyCreateForm();
            this.$emit("input", null);
            this.$emit("selected", null);
        },
    },
};
</script>
<style>
.business-lookup-create {
    border: none;
    border-left: 4px solid #66bb6a;
    background: #fbfff9;
}

.business-lookup-create-header {
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #2e7d32;
    display: flex;
    align-items: center;
}

.business-lookup-create-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
}
</style>
